import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { approvedScanUrl, qrScannerAllowedHosts, qrScannerEnabled, qrScannerParentOrigins } from '../utils/qrScanner';

export interface QrScannerOptions {
  enabled?: boolean;
  autoNavigate?: boolean;
  allowedHosts?: string[];
  allowedPaths?: RegExp[];
  parentOrigins?: string[];
  maxKeyGap?: number;
  minLength?: number;
  scanTimeout?: number;
  onScan?: (url: URL) => void;
}

const DUPLICATE_WINDOW = 2000;
const TRAILING_KEY_WINDOW = 500;
// Once an approved URL has started, tolerate scanner pauses so long URLs aren't dropped partway.
const SCANNING_KEY_GAP = 500;
// Clears the loading overlay if a full page load never happens (e.g. the browser blocks it).
const NAVIGATION_TIMEOUT = 20000;

type Editable = HTMLInputElement | HTMLTextAreaElement;

interface FieldSnapshot {
  element: Editable;
  value: string;
  selectionStart: number | null;
  selectionEnd: number | null;
}

const couldBeUrl = (text: string) => {
  const lower = text.toLowerCase();
  return lower.startsWith('http') || 'http'.startsWith(lower);
};

const snapshotOf = (target: EventTarget | null): FieldSnapshot | null => {
  if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLTextAreaElement)) return null;
  return {
    element: target,
    value: target.value,
    selectionStart: target.selectionStart,
    selectionEnd: target.selectionEnd
  };
};

// React tracks input values through the native setter, so assigning .value directly would leave its state stale.
const restore = ({ element, value, selectionStart, selectionEnd }: FieldSnapshot) => {
  const prototype = element instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(prototype, 'value')?.set?.call(element, value);
  element.dispatchEvent(new Event('input', { bubbles: true }));
  if (selectionStart !== null) element.setSelectionRange(selectionStart, selectionEnd);
};

// Scanners type long URLs over several seconds, so a scan is recognized as soon as an approved origin has come through.
const startsApprovedUrl = (text: string, allowedHosts: string[]) => {
  const origin = /^https?:\/\/[^/?#]+[/?#]/i.exec(text)?.[0];
  return !!origin && !!approvedScanUrl(origin, allowedHosts);
};

// USB scanners type like a keyboard, so a scan is a fast, uninterrupted burst of keys that forms an approved URL.
export const useQrScanner = (options: QrScannerOptions = {}) => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  const optionsRef = useRef(options);
  navigateRef.current = navigate;
  optionsRef.current = options;
  const [isScanning, setIsScanning] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const enabled = options.enabled !== false && qrScannerEnabled();

  useEffect(() => {
    if (!enabled) return undefined;

    let buffer = '';
    let lastKeyAt = 0;
    let field: FieldSnapshot | null = null;
    let timer: number | undefined;
    let lastScan = { href: '', at: 0 };
    let swallowTerminatorUntil = 0;
    let navigationTimer: number | undefined;
    let scanning = false;

    const setScanning = (value: boolean) => {
      if (scanning === value) return;
      scanning = value;
      setIsScanning(value);
    };

    const navigateTo = (url: URL) => {
      if (url.origin === window.location.origin) {
        navigateRef.current(url.pathname + url.search + url.hash);
        return;
      }
      // Full page loads (especially short links, which redirect first) can take a few seconds with no visible change.
      setIsNavigating(true);
      window.clearTimeout(navigationTimer);
      navigationTimer = window.setTimeout(() => setIsNavigating(false), NAVIGATION_TIMEOUT);
      // Only href is writable on a cross-origin top; it throws without user activation (e.g. forwarded scans) or under a sandbox.
      try {
        (window.top ?? window).location.href = url.href;
      } catch {
        window.location.assign(url.href);
      }
    };

    const handleScan = (text: string) => {
      const { allowedHosts = qrScannerAllowedHosts(), allowedPaths, autoNavigate = true, onScan } = optionsRef.current;
      const url = approvedScanUrl(text, allowedHosts, allowedPaths);
      if (!url) return false;
      const now = Date.now();
      if (url.href === lastScan.href && now - lastScan.at < DUPLICATE_WINDOW) return true;
      lastScan = { href: url.href, at: now };
      onScan?.(url);
      if (autoNavigate) navigateTo(url);
      return true;
    };

    const reset = () => {
      buffer = '';
      field = null;
      window.clearTimeout(timer);
      setScanning(false);
    };

    const complete = () => {
      const text = buffer;
      const typedInto = field;
      reset();
      if (text.length < (optionsRef.current.minLength ?? 15) || !handleScan(text)) return false;
      if (typedInto) restore(typedInto);
      return true;
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.isComposing || event.ctrlKey || event.metaKey || event.altKey) {
        reset();
        return;
      }
      const options = optionsRef.current;
      const maxKeyGap = scanning ? SCANNING_KEY_GAP : (options.maxKeyGap ?? 100);
      const scanTimeout = scanning ? SCANNING_KEY_GAP : (options.scanTimeout ?? 250);
      const now = event.timeStamp;

      // Scanners may end with Enter or Tab; swallow it only when it closes a valid scan.
      if (event.key === 'Enter' || event.key === 'Tab') {
        const closesScan = buffer ? now - lastKeyAt <= scanTimeout && complete() : now < swallowTerminatorUntil;
        reset();
        if (closesScan) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }
      if (event.key.length !== 1) return;

      if (buffer && now - lastKeyAt > maxKeyGap) reset();
      if (!couldBeUrl(buffer + event.key)) reset();
      if (!couldBeUrl(buffer + event.key)) return;

      if (!buffer) field = snapshotOf(event.target);
      buffer += event.key;
      lastKeyAt = now;
      if (!scanning && startsApprovedUrl(buffer, optionsRef.current.allowedHosts ?? qrScannerAllowedHosts()))
        setScanning(true);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (complete()) swallowTerminatorUntil = performance.now() + TRAILING_KEY_WINDOW;
      }, scanTimeout);
    };

    const onMessage = (event: MessageEvent) => {
      if (window.parent === window || event.source !== window.parent) return;
      if (!(optionsRef.current.parentOrigins ?? qrScannerParentOrigins()).includes(event.origin)) return;
      const { action, arguments: args } = event.data ?? {};
      if (action === 'qrScan' && typeof args?.url === 'string') handleScan(args.url);
    };

    // Coming back through the back/forward cache restores the page as it was left, overlay included.
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      window.clearTimeout(navigationTimer);
      setIsNavigating(false);
    };

    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('message', onMessage);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('message', onMessage);
      window.removeEventListener('pageshow', onPageShow);
      window.clearTimeout(timer);
      window.clearTimeout(navigationTimer);
    };
  }, [enabled]);

  return { isScanning, isNavigating };
};
